import { useQuery } from "@tanstack/react-query";
import type {
  ActivitiesResponse,
  Activity,
} from "@/hooks/api/use-account-activities";
import { api, generateQueryKey } from "@/lib/api";

/**
 * Hook to fetch activities aggregated across all accounts associated with a person
 * @param personId - The person ID to fetch activities for
 * @returns Aggregated activities sorted by date, loading state, and error
 */
export function usePersonActivities(personId?: string) {
  return useQuery<Activity[]>({
    queryKey: generateQueryKey(
      `datahub/persons/${encodeURIComponent(personId || "")}/activities/`,
    ),
    queryFn: async () => {
      const response = await api.get<ActivitiesResponse>(
        `datahub/persons/${encodeURIComponent(personId || "")}/activities/`,
      );

      // Transform the response from headers/data format to an array of objects
      const { headers, data } = response;

      return data.map((row) => {
        const activity: Partial<Activity> = {};
        headers.forEach((header, index) => {
          const value = row[index];
          (activity as Record<string, ActivityValue>)[header] = value;
        });
        return activity as Activity;
      });
    },
    enabled: !!personId,
  });
}

// Define the possible value types that can appear in the data array
type ActivityValue =
  | string
  | number
  | boolean
  | null
  | string[]
  | Array<{ id: string; role_in_touchpoint?: string }>;
